import _ from 'lodash'
import CONSTANTS from 'utils/constants'
import { checkMFAForce, ssoMiddleware } from './sso'
import { Dto } from '@lmc/ts-common/lib/utils/errors'

let requestAbortController: AbortController

type RequestMethod = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH'
type RequestContentType = 'application/json' | 'multipart/form-data'
type RequestResponseType = 'json' | 'text' | 'arrayBuffer'
type LmcRequestParams = Parameters<typeof lmcRequest>[0]

async function lmcRequest(params: {
  body?: any
  contentType?: RequestContentType
  method: RequestMethod
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
}) {
  if (params.newSignal === undefined) {
    params.newSignal = false
  }

  const baseUrl = `${
    params?.isRota ? CONSTANTS.VITE_APP_ROTA_API : CONSTANTS.VITE_APP_CM_API
  }${params.url.startsWith('/') ? '' : '/'}`
  const fullUrl = `${baseUrl}${params.url}`

  const headers: HeadersInit = {
    Accept: 'application/json',
  }
  if (params.contentType) {
    headers['Content-Type'] = params.contentType
  }

  if (params.newSignal || !requestAbortController) {
    if (requestAbortController) {
      requestAbortController.abort()
    }
    requestAbortController = new AbortController()
  }
  const requestOptions: RequestInit = {
    credentials: 'include',
    method: params.method,
    headers,
    signal: requestAbortController.signal,
  }
  if (params.body) {
    requestOptions.body = params.body
  }

  try {
    const request = new Request(fullUrl, requestOptions)
    const response = await fetch(request)
    await ssoMiddleware(response)

    // handle response body
    let result: ArrayBuffer | string | Record<string, any>

    if (params.responseType === 'arrayBuffer') {
      result = await response.clone().arrayBuffer()
    } else if (params.responseType === 'json') {
      result = await response.clone().json()

      // } else if (params.responseType === 'text') {
    } else {
      result = await response.clone().text()
    }

    return {
      response,
      result,
    }
  } catch (_e: any) {
    const e: Error = _e
    throw e
  }
}

export const getJSON = async (params: {
  match?: boolean
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
  customErrorHandling?: boolean
}) => {
  if (params.responseType === undefined) {
    params.responseType = 'json'
  }
  if (params.match === undefined) {
    params.match = true
  }

  if (params.match ? !params.url.match(/\?/) : false) {
    if (params.url.match(/residents$/)) {
      params.url += '?anyStatus=true'
    }
  }

  const params2: LmcRequestParams = {
    contentType: 'application/json',
    method: 'GET',
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url,
    isRota: params?.isRota,
  }

  try {
    const request = await lmcRequest(params2)
    const { response } = request
    const result = request.result as Record<string, any>

    checkMFAForce(result as Dto)

    if (!params?.customErrorHandling && !response.ok && result.error) {
      throw new Error(result.error)
    }

    return result as any
  } catch (_e: any) {
    const e: Error = _e
    console.log(e)
    throw e
  }
}

export const get = async (params: {
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
}) => {
  if (params.responseType === undefined) {
    params.responseType = 'json'
  }

  const params2: LmcRequestParams = {
    contentType: 'application/json',
    method: 'GET',
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url,
  }

  const request = await lmcRequest(params2)
  const { response } = request
  const result = request.result as Record<string, any>

  checkMFAForce(result as Dto)

  if (!response.ok) {
    throw response
  }

  return result as any
}

export const postJSON = async (params: {
  body?: any
  contentType?: RequestContentType
  json?: any
  method?: RequestMethod
  newSignal?: boolean
  responseType?: RequestResponseType
  url: string
  isRota?: boolean
}) => {
  if (params.method === undefined) {
    params.method = 'POST'
  }
  if (params.contentType === undefined) {
    params.contentType = 'application/json'
  }
  if (params.contentType === 'multipart/form-data') {
    delete params.contentType
  }
  if (!params.responseType) {
    params.responseType = 'json'
  }
  const params2: LmcRequestParams = {
    body:
      params.body ?? (params.json ? JSON.stringify(params.json) : undefined),
    contentType: params.contentType,
    method: params.method,
    newSignal: params.newSignal,
    responseType: params.responseType,
    url: params.url,
    isRota: params?.isRota,
  }

  const request = await lmcRequest(params2)

  const { response } = request
  const result = request.result as any

  checkMFAForce(result)

  if (!response.ok) {
    let serverError: string
    if (result.message) {
      throw result
    } else if (response.status === 403) {
      serverError = "Oops! You don't have access to this feature."
    } else if (response.status === 400) {
      serverError = handleValidationError(result)
    } else {
      serverError =
        'Oops! Something went wrong. Please refresh the page and try again.'
    }

    throw new Error(serverError)
  }

  return result
}

export const setFirstLogin = () =>
  new Promise((resolve, reject) => {
    postJSON({
      url: `/api/reports/user/firstlogin`,
    })
      .catch((err) => {
        console.log(err)
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          message: 'Could not set first login.',
        })
      })
      .then((result) => {
        resolve(result)
        console.log(result)
      })
  })

export const deleteItem = async (id: string, list: string) => {
  const url = `/api/list/${list}/${id}`
  return new Promise((resolve, reject) => {
    const params: LmcRequestParams = {
      method: 'DELETE',
      url,
    }
    lmcRequest(params)
      .then((request) => resolve(request.response))
      .catch((e) => {
        console.log(e)
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          message: 'The item could not be deleted at this time.',
        })
      })
  })
}

function handleValidationError(error: any) {
  if (error?.detail && error?.detail?.password) {
    return _.capitalize(error.detail.password.error)
  }
  if (error?.error?.userExists) {
    return error.error
  }
  if (typeof error?.error === 'string') {
    return error.error
  }
  if (typeof error?.message === 'string') {
    return error.message
  }
  return 'Oops! There were validation errors. Please check all information is correct and try again.'
}
